"use client";

import { memo, useMemo, useRef, useEffect, useCallback, useState } from "react";
import { useA2UIComponent, type A2UIComponentProps } from "@a2ui/react";
import { resolveEnum } from "./utils";
import type * as EChartsNamespace from "echarts";
import type { IChartApi } from "lightweight-charts";

const PALETTE = [
  "#6366f1", "#06b6d4", "#f59e0b", "#ef4444",
  "#10b981", "#8b5cf6", "#ec4899", "#64748b",
];

type ChartType =
  | "bar" | "horizontal_bar" | "line" | "area" | "scatter"
  | "pie" | "doughnut" | "radar" | "funnel" | "gauge"
  | "candlestick" | "sparkline";

interface DatasetInput {
  label?: string;
  data?: unknown[];
  color?: string;
}

function resolveStr(v: unknown): string {
  if (typeof v === "string") return v;
  if (v && typeof v === "object" && "literalString" in v)
    return (v as { literalString: string }).literalString;
  return String(v ?? "");
}

function resolveArray(v: unknown): unknown[] {
  if (Array.isArray(v)) return v;
  if (v && typeof v === "object") {
    const o = v as Record<string, unknown>;
    if ("listValue" in o) {
      const lv = o.listValue;
      if (Array.isArray(lv)) return lv;
      if (lv && typeof lv === "object" && "values" in (lv as any))
        return Array.isArray((lv as any).values) ? (lv as any).values : [];
    }
    if ("values" in o && Array.isArray(o.values)) return o.values;
  }
  return [];
}

function num(v: unknown): number {
  if (typeof v === "number") return v;
  if (typeof v === "string") return Number(v) || 0;
  if (v && typeof v === "object") {
    const o = v as Record<string, unknown>;
    if ("literalString" in o) return Number(o.literalString) || 0;
    if ("literalFloat" in o) return Number(o.literalFloat) || 0;
    if ("literalNumber" in o) return Number(o.literalNumber) || 0;
    if ("numberValue" in o) return Number(o.numberValue) || 0;
    if ("floatValue" in o) return Number(o.floatValue) || 0;
  }
  return Number(v) || 0;
}

function clr(idx: number, override?: string): string {
  return override || PALETTE[idx % PALETTE.length];
}

function mergeCandlestickData(
  labels: string[],
  datasets: DatasetInput[],
): Array<{ name: string; open: number; close: number; high: number; low: number; fill: string }> {
  const ds = datasets[0];
  if (!ds) return [];
  const arr = resolveArray(ds.data);
  return arr.map((raw, i) => {
    const pt = (raw && typeof raw === "object" ? raw : {}) as Record<string, unknown>;
    const o = num(pt.open ?? pt.o);
    const c = num(pt.close ?? pt.c);
    const h = num(pt.high ?? pt.h);
    const l = num(pt.low ?? pt.l);
    return {
      name: labels[i] ?? `${i}`,
      open: o, close: c,
      high: h || Math.max(o, c),
      low: l || Math.min(o, c),
      fill: c >= o ? "#10b981" : "#ef4444",
    };
  });
}

// ---------------------------------------------------------------------------
// ECharts option builder — Canvas rendering is immune to A2UI's all:revert
// ---------------------------------------------------------------------------

interface ThemeColors {
  text: string;
  grid: string;
  tooltipBg: string;
  tooltipBorder: string;
  tooltipText: string;
}

function themeColors(dark: boolean): ThemeColors {
  return dark
    ? { text: "#94a3b8", grid: "#334155", tooltipBg: "#1e293b", tooltipBorder: "#334155", tooltipText: "#e2e8f0" }
    : { text: "#64748b", grid: "#e2e8f0", tooltipBg: "#ffffff", tooltipBorder: "#e2e8f0", tooltipText: "#1e293b" };
}

function buildOption(
  chartType: ChartType,
  labels: string[],
  datasets: DatasetInput[],
  value: number,
  max: number,
  dark: boolean,
): EChartsNamespace.EChartsOption {
  const t = themeColors(dark);

  const tooltip = {
    backgroundColor: t.tooltipBg,
    borderColor: t.tooltipBorder,
    textStyle: { color: t.tooltipText, fontSize: 11 },
  };

  const legend: any = datasets.length > 1
    ? { show: true, textStyle: { color: t.text, fontSize: 10 } }
    : { show: false };

  const grid = { top: 30, right: 10, bottom: 24, left: 50, containLabel: false };

  const catAxis = (data: string[]): any => ({
    type: "category", data,
    axisLabel: { color: t.text, fontSize: 10, hideOverlap: true },
    axisLine: { lineStyle: { color: t.grid } },
  });

  const valAxis = (): any => ({
    type: "value",
    axisLabel: { color: t.text, fontSize: 10 },
    splitLine: { lineStyle: { color: t.grid, type: "dashed" } },
  });

  switch (chartType) {
    case "bar":
      return {
        tooltip: { ...tooltip, trigger: "axis" },
        legend, grid,
        xAxis: catAxis(labels),
        yAxis: valAxis(),
        series: datasets.map((ds, i) => ({
          name: ds.label || "value",
          type: "bar" as const,
          data: resolveArray(ds.data).map(num),
          itemStyle: { color: clr(i, ds.color), borderRadius: [2, 2, 0, 0] },
        })),
      };

    case "horizontal_bar":
      return {
        tooltip: { ...tooltip, trigger: "axis" },
        legend,
        grid: { ...grid, left: 70 },
        xAxis: valAxis(),
        yAxis: catAxis(labels),
        series: datasets.map((ds, i) => ({
          name: ds.label || "value",
          type: "bar" as const,
          data: resolveArray(ds.data).map(num),
          itemStyle: { color: clr(i, ds.color), borderRadius: [0, 2, 2, 0] },
        })),
      };

    case "line":
      return {
        tooltip: { ...tooltip, trigger: "axis" },
        legend, grid,
        xAxis: { ...catAxis(labels), boundaryGap: false },
        yAxis: valAxis(),
        series: datasets.map((ds, i) => ({
          name: ds.label || "value",
          type: "line" as const,
          data: resolveArray(ds.data).map(num),
          smooth: true,
          showSymbol: false,
          lineStyle: { width: 2, color: clr(i, ds.color) },
          itemStyle: { color: clr(i, ds.color) },
        })),
      };

    case "area":
      return {
        tooltip: { ...tooltip, trigger: "axis" },
        legend, grid,
        xAxis: { ...catAxis(labels), boundaryGap: false },
        yAxis: valAxis(),
        series: datasets.map((ds, i) => ({
          name: ds.label || "value",
          type: "line" as const,
          data: resolveArray(ds.data).map(num),
          smooth: true,
          showSymbol: false,
          lineStyle: { width: 2, color: clr(i, ds.color) },
          itemStyle: { color: clr(i, ds.color) },
          areaStyle: { color: clr(i, ds.color), opacity: 0.15 },
        })),
      };

    case "scatter":
      return {
        tooltip: { ...tooltip, trigger: "item" },
        legend, grid,
        xAxis: catAxis(labels),
        yAxis: valAxis(),
        series: datasets.map((ds, i) => ({
          name: ds.label || "value",
          type: "scatter" as const,
          data: resolveArray(ds.data).map(num),
          itemStyle: { color: clr(i, ds.color) },
          symbolSize: 8,
        })),
      };

    case "pie":
    case "doughnut": {
      const ds = datasets[0];
      const arr = ds ? resolveArray(ds.data) : [];
      return {
        tooltip: { ...tooltip, trigger: "item" },
        legend: {
          show: true, orient: "horizontal" as const, bottom: 0,
          textStyle: { color: t.text, fontSize: 10 },
        },
        series: [{
          type: "pie" as const,
          radius: chartType === "doughnut" ? ["50%", "75%"] : ["0%", "75%"],
          center: ["50%", "45%"],
          data: arr.map((v, i) => ({
            name: labels[i] ?? `${i}`,
            value: num(v),
            itemStyle: { color: clr(i) },
          })),
          label: { show: true, formatter: "{b}: {d}%", color: t.text, fontSize: 10 },
          labelLine: { show: true },
          itemStyle: { borderColor: "transparent", borderWidth: 2 },
        }],
      };
    }

    case "radar": {
      const allVals = datasets.flatMap(ds => resolveArray(ds.data).map(num));
      const maxVal = Math.max(...allVals, 1);
      return {
        tooltip: { ...tooltip, trigger: "item" },
        legend,
        radar: {
          indicator: labels.map(l => ({ name: l, max: maxVal * 1.2 })),
          axisName: { color: t.text, fontSize: 10 },
          splitLine: { lineStyle: { color: t.grid } },
          splitArea: { show: false },
          axisLine: { lineStyle: { color: t.grid } },
        } as any,
        series: [{
          type: "radar" as const,
          data: datasets.map((ds, i) => ({
            name: ds.label || "value",
            value: resolveArray(ds.data).map(num),
            lineStyle: { color: clr(i, ds.color), width: 2 },
            areaStyle: { color: clr(i, ds.color), opacity: 0.2 },
            itemStyle: { color: clr(i, ds.color) },
          })),
        }],
      };
    }

    case "funnel": {
      const ds = datasets[0];
      const arr = ds ? resolveArray(ds.data) : [];
      return {
        tooltip: { ...tooltip, trigger: "item" },
        series: [{
          type: "funnel" as const,
          data: arr.map((v, i) => ({
            name: labels[i] ?? `${i}`,
            value: num(v),
            itemStyle: { color: clr(i) },
          })),
          label: { show: true, position: "inside" as const, color: "#fff", fontSize: 11 },
          gap: 2,
          left: "10%", right: "10%", top: 8, bottom: 8,
        }],
      };
    }

    case "gauge":
      return {
        series: [{
          type: "gauge" as const,
          startAngle: 180,
          endAngle: 0,
          min: 0,
          max,
          progress: { show: true, width: 16, itemStyle: { color: "#6366f1" } },
          axisLine: { lineStyle: { width: 16, color: [[1, t.grid]] } },
          axisTick: { show: false },
          splitLine: { show: false },
          axisLabel: { show: false },
          pointer: { show: false },
          title: { show: false },
          detail: {
            valueAnimation: true,
            fontSize: 20,
            fontWeight: "bold" as const,
            color: t.text,
            offsetCenter: [0, "-10%"],
            formatter: `{value} / ${max}`,
          },
          data: [{ value }],
          center: ["50%", "70%"],
          radius: "90%",
        }] as any,
      };

    case "sparkline":
      return {
        grid: { top: 2, right: 2, bottom: 2, left: 2 },
        xAxis: { type: "category" as const, data: labels, show: false, boundaryGap: false },
        yAxis: { type: "value" as const, show: false },
        series: datasets.map((ds, i) => ({
          type: "line" as const,
          data: resolveArray(ds.data).map(num),
          smooth: true,
          showSymbol: false,
          lineStyle: { width: 2, color: clr(i, ds.color) },
          itemStyle: { color: clr(i, ds.color) },
        })),
      };

    default:
      return buildOption("bar", labels, datasets, value, max, dark);
  }
}

// ---------------------------------------------------------------------------
// ECharts React wrapper — init/dispose lifecycle via useEffect + ResizeObserver
// ---------------------------------------------------------------------------

function EChartsChart({
  chartType, labels, datasets, value, max, height,
}: {
  chartType: ChartType;
  labels: string[];
  datasets: DatasetInput[];
  value: number;
  max: number;
  height: number;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    let disposed = false;
    let chart: EChartsNamespace.ECharts | undefined;
    let ro: ResizeObserver | undefined;

    import("echarts").then((echarts) => {
      if (disposed || !el) return;

      const dark =
        document.documentElement.classList.contains("dark") ||
        window.matchMedia("(prefers-color-scheme: dark)").matches;

      chart = echarts.init(el);
      chart.setOption(buildOption(chartType, labels, datasets, value, max, dark));
      setReady(true);

      ro = new ResizeObserver(() => chart?.resize());
      ro.observe(el);
    });

    return () => {
      disposed = true;
      ro?.disconnect();
      chart?.dispose();
    };
  }, [chartType, labels, datasets, value, max]);

  return <div ref={containerRef} style={{ width: "100%", height }} />;
}

// ---------------------------------------------------------------------------
// Candlestick — lightweight-charts (Canvas, unchanged)
// ---------------------------------------------------------------------------

function RenderCandlestickChart({
  data,
  height,
}: {
  data: Array<{ name: string; open: number; close: number; high: number; low: number }>;
  height: number;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<IChartApi | null>(null);

  const lwData = useMemo(() => {
    const baseDate = new Date();
    baseDate.setHours(0, 0, 0, 0);
    return data.map((d, i) => ({
      time: (Math.floor(baseDate.getTime() / 1000) + i * 3600) as any,
      open: d.open,
      high: d.high,
      low: d.low,
      close: d.close,
      _label: d.name,
    }));
  }, [data]);

  const labelMap = useMemo(() => {
    const m = new Map<number, string>();
    lwData.forEach((d) => m.set(d.time as number, d._label));
    return m;
  }, [lwData]);

  const timeFormatter = useCallback(
    (t: number) => labelMap.get(t) ?? "",
    [labelMap],
  );

  useEffect(() => {
    const el = containerRef.current;
    if (!el || lwData.length === 0) return;

    let disposed = false;
    let chart: IChartApi | undefined;
    let ro: ResizeObserver | undefined;

    import("lightweight-charts").then(({ createChart, CandlestickSeries, ColorType }) => {
      if (disposed || !el) return;

      const isDark = document.documentElement.classList.contains("dark") ||
        window.matchMedia("(prefers-color-scheme: dark)").matches;

      chart = createChart(el, {
        width: el.clientWidth,
        height,
        layout: {
          background: { type: ColorType.Solid, color: "transparent" },
          textColor: isDark ? "#94a3b8" : "#64748b",
          fontSize: 10,
        },
        grid: {
          vertLines: { color: isDark ? "#334155" : "#e2e8f0" },
          horzLines: { color: isDark ? "#334155" : "#e2e8f0" },
        },
        timeScale: {
          borderColor: isDark ? "#334155" : "#e2e8f0",
          tickMarkFormatter: timeFormatter,
        },
        rightPriceScale: {
          borderColor: isDark ? "#334155" : "#e2e8f0",
        },
        crosshair: { mode: 0 },
      });

      const series = chart.addSeries(CandlestickSeries, {
        upColor: "#10b981",
        downColor: "#ef4444",
        wickUpColor: "#10b981",
        wickDownColor: "#ef4444",
        borderVisible: false,
      });

      series.setData(lwData);
      chart.timeScale().fitContent();
      chartRef.current = chart;

      ro = new ResizeObserver((entries) => {
        const { width: w } = entries[0].contentRect;
        if (w > 0) chart?.applyOptions({ width: w });
      });
      ro.observe(el);
    });

    return () => {
      disposed = true;
      ro?.disconnect();
      if (chartRef.current) {
        chartRef.current.remove();
        chartRef.current = null;
      }
    };
  }, [lwData, height, timeFormatter]);

  if (data.length === 0) return null;
  return <div ref={containerRef} style={{ width: "100%", height }} />;
}

// ---------------------------------------------------------------------------
// Exported component
// ---------------------------------------------------------------------------

function ChartTitle({ title }: { title: string }) {
  if (!title) return null;
  return (
    <p className="text-sm font-medium text-foreground mb-1 truncate">{title}</p>
  );
}

export const BeeOSChart = memo(function BeeOSChart({
  node,
  surfaceId,
}: A2UIComponentProps<any>) {
  const { resolveString } = useA2UIComponent(node, surfaceId);
  const props = node.properties as Record<string, unknown>;

  const chartType = resolveEnum<ChartType>(props.chartType, "bar");
  const title = resolveStr(props.title);
  const labels = useMemo(
    () => resolveArray(props.labels).map((l) => resolveStr(l)),
    [props.labels],
  );
  const datasets = useMemo(
    () =>
      resolveArray(props.datasets).map((ds: any) => ({
        label: resolveStr(ds?.label ?? ds?.name),
        data: resolveArray(ds?.data),
        color: ds?.color ? resolveStr(ds.color) : undefined,
      })) as DatasetInput[],
    [props.datasets],
  );
  const value = typeof props.value === "number" ? props.value : Number(props.value) || 0;
  const max = typeof props.max === "number" ? props.max : Number(props.max) || 100;

  const weightStyle: React.CSSProperties =
    node.weight !== undefined
      ? ({ "--weight": node.weight } as React.CSSProperties)
      : {};

  const isCompact = chartType === "sparkline";
  const isPie = chartType === "pie" || chartType === "doughnut";
  const isCandlestick = chartType === "candlestick";
  const chartHeight = isCompact ? 48 : isPie ? 220 : isCandlestick ? 280 : 200;

  const candlestickData = useMemo(
    () => (isCandlestick ? mergeCandlestickData(labels, datasets) : []),
    [isCandlestick, labels, datasets],
  );

  if (isCandlestick) {
    return (
      <div className="a2ui-chart" style={weightStyle}>
        <ChartTitle title={title} />
        <RenderCandlestickChart data={candlestickData} height={chartHeight} />
      </div>
    );
  }

  return (
    <div className="a2ui-chart" style={weightStyle}>
      <ChartTitle title={title} />
      <EChartsChart
        chartType={chartType}
        labels={labels}
        datasets={datasets}
        value={value}
        max={max}
        height={chartHeight}
      />
    </div>
  );
});
